import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface NodeData {
  currentReward: number;
  rewardsHistory: Array<{
    id: string;
    reward_change: number;
    snapshot_time: string;
  }>;
  faults: number;
  hardFaults: number;
  eligibility: number;
  stake: number;
  lockedAmt: number;
}

export function useNodeData(address: string | null) {
  return useQuery({
    queryKey: ["dashboardData", address],
    queryFn: async (): Promise<NodeData | null> => {
      console.log('Fetching dashboard data for address:', address);
      if (!address) return null;

      // Fetch current reward from provisioners table
      const { data: provisionerData, error: provisionerError } = await supabase
        .from('provisioners')
        .select('current_reward')
        .eq('address', address)
        .maybeSingle();

      if (provisionerError) {
        console.error('Error fetching metrics:', provisionerError);
        throw provisionerError;
      }

      // Fetch latest snapshot for faults, eligibility, stake, and locked amount
      const { data: latestSnapshot, error: snapshotError } = await supabase
        .from('provisioner_snapshots')
        .select('faults, hard_faults, eligibility, stake, locked_amt')
        .eq('address', address)
        .order('snapshot_time', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (snapshotError) {
        console.error('Error fetching faults:', snapshotError);
        throw snapshotError;
      }

      // Fetch rewards history
      const { data: history, error: historyError } = await supabase
        .from('provisioner_snapshots')
        .select('id, reward_change, snapshot_time')
        .eq('address', address)
        .order('snapshot_time', { ascending: false })
        .limit(10);

      if (historyError) {
        console.error('Error fetching history:', historyError);
        throw historyError;
      }

      return {
        currentReward: provisionerData?.current_reward || 0,
        rewardsHistory: history || [],
        faults: latestSnapshot?.faults || 0,
        hardFaults: latestSnapshot?.hard_faults || 0,
        eligibility: latestSnapshot?.eligibility || 0,
        stake: latestSnapshot?.stake || 0,
        lockedAmt: latestSnapshot?.locked_amt || 0
      };
    },
    enabled: !!address,
    staleTime: 60 * 1000, // Data considered fresh for 1 minute
    refetchInterval: 60 * 1000, // Refetch every minute
  });
}